import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ExperienceBanner = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <section className="relative py-20 px-6 overflow-hidden bg-[#0a0a0a]">
      {/* Background with subtle green gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#6B7F5C]/5 to-transparent" />

      <motion.div
        ref={ref}
        className="relative max-w-6xl mx-auto"
        initial={{ opacity: 0, y: 60 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        {/* Glass morphism card */}
        <motion.div
          className="relative bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl border border-white/10"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#C9A86A]/20 to-[#6B7F5C]/20 opacity-50" />

          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-6 sm:p-10 md:p-16">
            {/* Left side - Text content */}
            <motion.div
              className="flex flex-col justify-center space-y-6"
              initial={{ x: -50, opacity: 0 }}
              animate={inView ? { x: 0, opacity: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              {/* Icon */}
              <motion.div
                className="inline-block"
                initial={{ scale: 0, rotate: -180 }}
                animate={inView ? { scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              >
                <div className="w-16 h-16 bg-[#C9A86A] rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </motion.div>

              {/* Title */}
              <h2 className="text-4xl md:text-5xl font-light">
                <span className="text-[#C9A86A] font-medium">
                  Compartí tu
                </span>
                <br />
                <span className="text-white">Experiencia</span>
              </h2>

              {/* Description */}
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                Ahora podés vivir la experiencia MAUMA junto a la persona que quieras.
              </p>

              <p className="text-base text-gray-400">
                Nuestra sala con camilla doble te permite disfrutar de un momento único de relax en compañía.
              </p>

              {/* Features */}
              <div className="flex flex-col gap-3 pt-4">
                {[
                  'Sala privada con camilla doble',
                  'Atención personalizada',
                  'Ambiente de total armonía'
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ x: -20, opacity: 0 }}
                    animate={inView ? { x: 0, opacity: 1 } : {}}
                    transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  >
                    <div className="w-2 h-2 bg-[#C9A86A] rounded-full" />
                    <span className="text-gray-300">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right side - Image */}
            <motion.div
              className="relative h-64 md:h-96"
              initial={{ x: 50, opacity: 0 }}
              animate={inView ? { x: 0, opacity: 1 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-[#C9A86A]/30 to-[#6B7F5C]/30 rounded-2xl"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              <img
                src="https://customer-assets.emergentagent.com/job_headspace-admin/artifacts/pi0syagp_Imagen%20de%20WhatsApp%202025-05-09%20a%20las%2015.25.13_63b893e6.jpg"
                alt="Experiencia compartida"
                className="relative w-full h-full object-cover object-center rounded-2xl shadow-xl"
              />

              {/* Floating badge */}
              <motion.div
                className="absolute -top-4 -right-4 bg-[#C9A86A] text-white px-6 py-3 rounded-full shadow-2xl"
                initial={{ scale: 0, rotate: -45 }}
                animate={inView ? { scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <span className="text-sm font-medium">¡NUEVO!</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ExperienceBanner;
